/**
 * Section Designations Service
 * 
 * Central service for accessing rolled steel section data
 * Aggregates all section types and provides lookup functions
 */

import { rolledSteelBeamSections, RolledSteelBeamData } from './sections/rolledSteelBeams';
import { rolledSteelChannelSections, RolledSteelChannelData } from './sections/rolledSteelChannels';
import { rolledSteelEqualAngleSections, RolledSteelEqualAngleData } from './sections/rolledSteelEqualAngles';
import { rolledSteelUnequalAngles, RolledSteelUnequalAngleData } from './sections/rolledSteelUnequalAngles';
import { rolledSteelTeeBars, RolledSteelTeeBarData } from './sections/rolledSteelTeeBars';
import { rolledSteelBulbAngles, RolledSteelBulbAngleData } from './sections/rolledSteelBulbAngles';
import type { SectionType } from '../types/property';

/**
 * Union type of all possible section data types
 */
export type SectionDesignationData = 
  | RolledSteelBeamData 
  | RolledSteelChannelData 
  | RolledSteelEqualAngleData 
  | RolledSteelUnequalAngleData 
  | RolledSteelTeeBarData 
  | RolledSteelBulbAngleData;

/**
 * Designation option for dropdowns
 */
export interface DesignationOption {
  designation: string;
  data: SectionDesignationData;
}

/**
 * Section type mapping to their data arrays
 */
const sectionTypeDataMap: Record<string, SectionDesignationData[]> = {
  'I-Beam': rolledSteelBeamSections,
  'Channel': rolledSteelChannelSections,
  'Equal Angle': rolledSteelEqualAngleSections,
  'Unequal Angle': rolledSteelUnequalAngles,
  'Tee Bar': rolledSteelTeeBars,
  'Bulb Angle': rolledSteelBulbAngles,
  'Custom Section': [], // Custom sections don't have predefined designations
};

/**
 * Get designation options for a specific section type
 * @param sectionType - The type of section (e.g., 'I-Beam', 'Channel')
 * @returns Array of designation options with full data
 */
export function getDesignationsForSectionType(sectionType: SectionType): DesignationOption[] {
  const sections = sectionTypeDataMap[sectionType];
  
  if (!sections) {
    console.warn(`Unknown section type: ${sectionType}`);
    return [];
  }

  return sections.map(section => ({
    designation: section.designation,
    data: section
  }));
}

/**
 * Get all available section types
 * @returns Array of section type names
 */
export function getAvailableSectionTypes(): string[] {
  return Object.keys(sectionTypeDataMap);
}

/**
 * Find designation data by designation name
 * @param designation - The designation name (e.g., 'ISJB 150')
 * @returns Section data if found, null otherwise
 */
export function getDesignationData(designation: string): SectionDesignationData | null {
  for (const sections of Object.values(sectionTypeDataMap)) {
    const found = sections.find(s => s.designation === designation);
    if (found) return found;
  }
  return null;
}

/**
 * Get designation data for a specific section type and designation
 * @param sectionType - The type of section
 * @param designation - The designation name
 * @returns Section data if found, null otherwise
 */
export function getDesignationDataForType(
  sectionType: SectionType, 
  designation: string
): SectionDesignationData | null {
  const sections = sectionTypeDataMap[sectionType];
  if (!sections) return null;
  
  return sections.find(s => s.designation === designation) || null;
}

/**
 * Check if a designation exists
 * @param designation - The designation name to check
 * @returns true if designation exists, false otherwise
 */
export function designationExists(designation: string): boolean {
  return getDesignationData(designation) !== null;
}
